// External
import axios from "axios";
import moment from "moment-timezone"; // Updated to moment-timezone
import { saveAs } from "file-saver";
import { toast } from "react-toastify";
import { useQuery } from 'react-query';
import { AES, enc } from "crypto-js";
import "react-toastify/dist/ReactToastify.css";
import { useSelector, useDispatch } from "react-redux";
import React, { useEffect, useRef, useState, Suspense } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

// Internal
import css from "./single.css";
import { ThreeDots } from "react-loader-spinner";
import { logout, reset } from "../../../features/auth/authSlice";
import { setHeaderText } from "../../../features/headerText/headerTextSlice";
import { reset as invoiceReset } from "../../../features/invoice/invoiceSlice.js";
import { setNotification } from "../../../features/notification/notificationSlice.js";
import { reset as headerReset } from "../../../features/headerText/headerTextSlice.js";
import { reset as appSettingsReset } from "../../../features/appSettings/appSettingsSlice.js";
import tikgImage from "../../../images/singleinvoice/tick-green.svg";
import crssrImage from "../../../images/singleinvoice/cross-red.svg";
import ExpenseActions from "./ExpenseActions.jsx"; // Updated to ExpenseActions
import userTypeConsts from "../../../constants/userTypeConsts.js";

const fetchExpenseData = async ({ queryKey }) => {
  const [, id, token] = queryKey;
  if (!token) {
    throw new Error("User not authenticated");
  }
  const response = await axios.get(`/api/expense/single/${id}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

const ExpenseSingleView = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { encryptedId } = useParams();
  const decodedId = decodeURIComponent(encryptedId.replace(/%2F/g, "/"));
  const decryptedBytes = AES.decrypt(decodedId, "encryptionKey");
  const id = decryptedBytes.toString(enc.Utf8);
  const componentRef = useRef();
  const [expenseData, setExpenseData] = useState(null);
  const [error, setError] = useState(null);
  const [approveLoading, setApproveLoading] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);

  const user = useSelector((state) => state.auth.user);
  const token = user?.token || null;

  const { isLoading, refetch } = useQuery({
    queryKey: ["expenseData", id, token],
    queryFn: fetchExpenseData,
    enabled: !!token,
    onSuccess: (data) => {
      setExpenseData(data);
    },
    onError: (err) => {
      setError(err.message || "Failed to fetch expense data");
      toast.error(err.message || "Error fetching expense data", {
        position: "bottom-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    },
  });

  useEffect(() => {
    dispatch(setHeaderText("Expenses"));
    window.scrollTo(0, 0);
  }, [dispatch]);

  const downloadExpenseDoc = async (expfile) => {
    try {
      const response = await axios.get(`/api/expense/doc/download/${expfile}`, {
        responseType: "blob",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const pdfBlob = new Blob([response.data], { type: "application/pdf" });
      saveAs(pdfBlob, `${expfile}`);
    } catch (error) {
      console.error(error);
      global.config.slackMessage(error.toString());
      if (error.response?.data?.status === "deleted") {
        localStorage.removeItem("user");
        dispatch(logout());
        dispatch(reset());
        dispatch(invoiceReset());
        dispatch(headerReset());
        dispatch(appSettingsReset());
        toast.error("This user is deleted!", {
          position: "bottom-right",
          autoClose: 1500,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
        });
        navigate("/");
        return;
      }
      // Handle error
    }
  };


  const handleApprove = async () => {
    try {
      setApproveLoading(true);
      await axios.get(`/api/expense/approve/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      toast.success("Expense Approved", { position: "bottom-right", autoClose: 2000 });
      setExpenseData((prev) => ({
        ...prev,
        exp_status: true,
      }));
    } catch (err) {
      toast.error("Error approving expense");
    } finally {
      setApproveLoading(false);
    }
  };


  const handleReject = async () => {
    try {
      setRejectLoading(true);
      await axios.get(`/api/expense/reject/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      toast.success("Expense Rejected", { position: "bottom-right", autoClose: 2000 });
      setExpenseData((prev) => ({
        ...prev,
        exp_status: false,
      }));
    } catch (err) {
      toast.error("Error rejecting expense");
    } finally {
      setRejectLoading(false);
    }
  };

  // Render loading or error state


  if (error) {
    return <div className="error-message">{error}</div>;
  }

  return (
    <div className="single-ls-info expense-single-page">
      <div className="back-btn-ins">
        <Link to="/user/expense/overview" className="btnf1-si-s">
          Back to All Expenses
        </Link>
      </div>
      <div className="man-padd-for-siginvo">

        {isLoading ? (
          <div className="outter-load-table exp-outter-load-table">
            <ThreeDots
              height="80"
              width="80"
              radius="9"
              color="#6479f9"
              ariaLabel="three-dots-loading"
              wrapperStyle={{}}
              wrapperClassName=""
              visible={true}
            />
          </div>
        ) : (
          <>
            <div>
              <ExpenseActions
                expenseData={expenseData}
                setExpenseData={setExpenseData}
                componentRef={componentRef}
                token={token}
                refetch={refetch}
              />
            </div>
            <div className="invoice-main-body">
              <div className="invoice-detail-bs1" ref={componentRef}>
                <div className="main-head-bs11">
                  <div className="invoce-id-num">
                    <div className="invo-id">Category</div>
                    <div className="invo-num-1">
                      {expenseData?.exp_category?.exp_category_name || "N/A"}
                    </div>
                  </div>
                  <div className="invoce-id-type">
                    <div className="invo-id">Currency</div>
                    <div className="invo-num-1">
                      {expenseData?.exp_currency && (
                        <p className="form-input">
                          {/* {(new Intl.NumberFormat('en-US', { style: 'currency', currency: expenseData?.exp_currency, }).format(0).replace(/[\d.,]/g, ''))}{expenseData?.exp_currency} */}
                          {(() => {
                            const formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: expenseData?.exp_currency });
                            const formattedZero = formatter.format(0);
                            const symbol = formatter.resolvedOptions().currencySymbol || formattedZero.replace(/[\d.,]/g, ''); // Use resolved symbol if available
                            const currencyCode = expenseData.exp_currency;

                            // Check if symbol is effectively the same as the currency code (trim and normalize)
                            const normalizedSymbol = symbol.replace(/\s+/g, '').toUpperCase();
                            const normalizedCode = currencyCode.toUpperCase();

                            if (normalizedSymbol === normalizedCode) {
                              return symbol; // Show only the symbol/code if they match
                            }
                            return `${symbol} ${currencyCode}`; // Add space for clarity if different
                          })()}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="invoce-id-exdate">
                    <div className="invo-id">Date</div>
                    <div className="invo-num-1">
                      {expenseData
                        ? moment
                          .tz(expenseData.exp_date, global.config.getTimeZone())
                          .locale("en-gb")
                          .format("DD/MM/YYYY")
                        : "Loading..."}
                    </div>
                  </div>
                  <div className="invoce-id-exdate">
                    <div className="invo-id">Billable</div>
                    <div className="invo-num-1">
                      <p className="form-input">{expenseData?.exp_is_billable ? "Yes" : "No"}</p>
                    </div>
                  </div>
                </div>
                <div className="invo-from-to">
                  <div className="invo-from-sing">
                    <div className="from-sing-head">User</div>
                    <div className="name-sing-head">
                      {expenseData?.exp_user && (
                        <>
                          {expenseData.exp_user.first_name
                            ? `${expenseData.exp_user.first_name} ${expenseData.exp_user.middle_name || ''} ${expenseData.exp_user.last_name || ''}`
                            : `${expenseData.exp_user.company_first_name
                              ? expenseData.exp_user.company_first_name + (expenseData.exp_user.company_last_name ? ` ${expenseData.exp_user.company_last_name}` : '')
                              : expenseData.exp_user.company_name || ''}`
                          }
                        </>
                      )}
                    </div>
                  </div>
                  {expenseData?.exp_is_billable ? (
                    <>
                      <div className="invo-to-sing">
                        <div className="to-sing-head">Client</div>
                        <div className="toname-sing-head">
                          {expenseData?.exp_client && (
                            expenseData?.exp_client?.client_name || expenseData?.exp_client?.legal_entity_name || "Unnamed Client"
                          )}
                        </div>
                      </div>
                      <div className="invo-to-sing">
                        <div className="to-sing-head">Project</div>
                        <div className="toname-sing-head">
                          {expenseData?.exp_project && (
                            expenseData?.exp_project?.project_name || "Unnamed Project"
                          )}
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="invo-to-sing">
                      <div className="to-sing-head">Company</div>
                      <div className="toname-sing-head">
                        {expenseData?.exp_wx_company && (
                          expenseData?.exp_wx_company?.wx_company || expenseData?.exp_client?.legal_entity_company_name || "Unnamed Company"
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div className="table-outtter">
                  <div className="table-inner-outmain22">
                    <table
                      className="invo-single-tb1"
                      style={{ width: "100%", borderCollapse: "collapse" }}
                    >
                      <thead>
                        <tr className="mainborder-bottom">
                          <td style={{ width: "40%", paddingRight: "20px" }}>Description</td>
                          <td style={{ width: "20%", paddingRight: "20px" }}>Subtotal</td>
                          <td style={{ width: "20%", paddingRight: "20px" }}>Taxes %</td>
                          <td style={{ width: "20%", paddingRight: "20px" }}>Amount</td>
                        </tr>
                      </thead>
                      <tbody>
                        {expenseData ? (
                          <tr
                            valign="top"
                            className="border-bottomiteam infotablem"
                            key={expenseData._id}
                          >
                            <td style={{ maxWidth: "300px", paddingRight: "30px" }}>
                              {expenseData.exp_description || "N/A"}
                            </td>
                            <td>
                              {new Intl.NumberFormat("en-US", {
                                style: "currency",
                                currency: expenseData.exp_currency || "USD",
                              }).format(expenseData.exp_sub_total || 0)}
                            </td>
                            <td>{expenseData.exp_tax || 0}</td>
                            <td>
                              {new Intl.NumberFormat("en-US", {
                                style: "currency",
                                currency: expenseData.exp_currency || "USD",
                              }).format(expenseData.exp_amount || 0)}
                            </td>
                          </tr>
                        ) : (
                          <tr>
                            <td colSpan="4">No items available</td>
                          </tr>
                        )}
                        <tr className="vat-bordertotal reborder reborder-top">
                          <td className="mob-no-need"></td>
                          <td className="mob-no-need"></td>
                          <td>Subtotal</td>
                          <td className="tab-totalsubvat">
                            {expenseData
                              ? new Intl.NumberFormat("en-US", {
                                style: "currency",
                                currency: expenseData.exp_currency || "USD",
                              }).format(expenseData.exp_sub_total || 0)
                              : "N/A"}
                          </td>
                        </tr>
                        <tr className="vat-bordertotal reborder">
                          <td className="mob-no-need"></td>
                          <td className="mob-no-need"></td>
                          <td>Taxes</td>
                          <td className="tab-totalvat">
                            {expenseData
                              ? new Intl.NumberFormat("en-US", {
                                style: "currency",
                                currency: expenseData.exp_currency || "USD",
                              }).format(expenseData.exp_tax || 0)
                              : "N/A"}
                          </td>
                        </tr>
                        <tr className="total-bordertotal reborder">
                          <td className="mob-no-need"></td>
                          <td className="mob-no-need"></td>
                          <td className="invo-totalfd">Expense Total</td>
                          <td className="tab-totalamt">
                            {expenseData
                              ? new Intl.NumberFormat("en-US", {
                                style: "currency",
                                currency: expenseData.exp_currency || "USD",
                              }).format(expenseData.exp_amount || 0)
                              : "N/A"}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  {expenseData?.exp_note && (
                    <div className="other-comment">
                      <div className="other-head1">Other Comments</div>
                      <div className="other-subhead1">{expenseData.exp_note}</div>
                    </div>
                  )}
                  {expenseData?.exp_file_attch?.length > 0 && (
                    <div className="attachment-single-invi">
                      <div className="attachment-head">Attachments</div>
                      {expenseData.exp_file_attch.map((file, index) => (
                        <div
                          key={index}
                          className="the-src-pdf-file"
                          style={{ cursor: "pointer" }}
                        >
                          <div className="bottom-download-outer">
                            <div className="bottom-download-inner">
                              <div className="bottom-download-item">
                                <div className="attachment-file-sib-pname">
                                  {file.length > 15 ? (
                                    <span className="shortened-name" title={file}>
                                      {file.slice(0, 15)}...
                                    </span>
                                  ) : (
                                    file
                                  )}
                                </div>
                                <div className="attachment-file-sib-psize">
                                  {getFileExtension(file)?.toUpperCase()}
                                  {expenseData.exp_file_attch_size?.[index] && (
                                    <span> ({expenseData.exp_file_attch_size[index]})</span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div
                              className="bottom-download-inner-right"
                              onClick={() => downloadExpenseDoc(file)}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="12"
                                height="15"
                                viewBox="0 0 12 15"
                                fill="none"
                              >
                                <path
                                  d="M5.67409 13.8727C5.69551 13.8928 5.71891 13.9108 5.74395 13.9266L5.77888 13.9439L5.82304 13.9665L5.86918 13.9804L5.90971 13.9909C5.97034 14.003 6.03328 14.003 6.09425 13.9909L6.13478 13.9804L6.18091 13.9665L6.22507 13.9439L6.26 13.9266C6.28537 13.9108 6.30877 13.8928 6.33019 13.8727L10.8939 9.57954C11.0458 9.40745 11.0333 9.1549 10.8649 8.99674C10.6965 8.83858 10.4276 8.82682 10.2444 8.9695L6.46134 12.526V1.43331C6.46134 1.19406 6.25473 1 6 1C5.74527 1 5.53865 1.19406 5.53865 1.43331V12.526L1.75563 8.9695C1.57241 8.82682 1.30351 8.83858 1.13512 8.99674C0.966727 9.1549 0.954205 9.40745 1.10612 9.57954L5.67343 13.8727H5.67409Z"
                                  fill="#282828"
                                  stroke="#282828"
                                  strokeWidth="0.5"
                                />
                              </svg>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              {user && (user.userType === userTypeConsts.SUPER_ADMIN || user.userType === userTypeConsts.ADMIN) && (
                <div className="manage-detail-outer">
                  <div className="manage-detail-bs2">
                    <div className="invoice-manage-singlepg-head">Manage Expense</div>
                    <div className="single-invo-sattus">
                      <div className="invo-main-status-head">Expense Status</div>
                      <div className="invo-sig-st-btns">
                        <button
                          className={`Approve-singlebtn ${expenseData?.exp_status === true ? "approved" : ""}`}
                          onClick={handleApprove}
                          disabled={approveLoading || expenseData?.exp_status === true}
                          style={{ pointerEvents: approveLoading || expenseData?.exp_status ? "none" : "auto" }}
                        >
                          {approveLoading ? (
                            <ThreeDots
                              height="20"
                              width="20"
                              radius="9"
                              color="#6479f8"
                              ariaLabel="three-dots-loading"
                              wrapperStyle={{ display: "inline-block", verticalAlign: "middle" }}
                              visible={true}
                            />
                          ) : (
                            <>
                              {expenseData?.exp_status === true ? "Approved" : "Approve"}{" "}
                              <img src={tikgImage} alt="Approve" />
                            </>
                          )}
                        </button>

                        <button
                          className={`Reject-singlebtn ${expenseData?.exp_status === false ? "rejected" : ""}`}
                          onClick={handleReject}
                          disabled={rejectLoading || expenseData?.exp_status === false}
                          style={{ pointerEvents: rejectLoading || !expenseData?.exp_status ? "none" : "auto" }}
                        >
                          {rejectLoading ? (
                            <ThreeDots
                              height="20"
                              width="20"
                              radius="9"
                              color="#6479f8"
                              ariaLabel="three-dots-loading"
                              wrapperStyle={{ display: "inline-block", verticalAlign: "middle" }}
                              visible={true}
                            />
                          ) : (
                            <>
                              {expenseData?.exp_status === false ? "Rejected" : "Reject"}{" "}
                              <img src={crssrImage} alt="Reject" />
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Utility function to get file extension
const getFileExtension = (filename) => {
  return filename ? filename.split(".").pop() : null;
};

export default ExpenseSingleView;